import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Play } from 'lucide-react';
import PageTransition from './PageTransition';
import CustomCursor from './CustomCursor';

const films = [
  {
    id: 1,
    title: "Jamblu - Aras (live)",
    embedUrl: "https://www.youtube.com/embed/f6o3icwyAx0?si=ZjjJaAfK1sBXymD1",
    thumbnail: "https://img.youtube.com/vi/f6o3icwyAx0/maxresdefault.jpg"
  },
  {
    id: 2,
    title: "Jamblu feat Anand Viankara - We never fight (cause we never talk)",
    embedUrl: "https://www.youtube.com/embed/AnFHmWWFbWY?si=iKODM_nKdRoHDUMC",
    thumbnail: "https://img.youtube.com/vi/AnFHmWWFbWY/maxresdefault.jpg"
  },
  {
    id: 3,
    title: "Jamblu - My Dreams Are Better On Life Support",
    embedUrl: "https://www.youtube.com/embed/OJuiEudyo0Q?si=nyrMphyr261jdBrk",
    thumbnail: "https://img.youtube.com/vi/OJuiEudyo0Q/maxresdefault.jpg"
  },
  {
    id: 4,
    title: "Jamblu X Fursat - Music To Watch The World Burn",
    embedUrl: "https://www.youtube.com/embed/j9qGwiFgAAQ?si=fyBrBoPpVTPAqmK-",
    thumbnail: "https://img.youtube.com/vi/j9qGwiFgAAQ/maxresdefault.jpg"
  }
];

const FilmPage = () => {
  const navigate = useNavigate();
  const [activeVideo, setActiveVideo] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [backgroundIndex, setBackgroundIndex] = useState(0);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const videoModalRef = useRef(null);
  const containerRef = useRef(null);

  // Track mouse for parallax/swivel effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (containerRef.current) {
        setMousePos({
          x: (e.clientX / window.innerWidth - 0.5) * 20,
          y: (e.clientY / window.innerHeight - 0.5) * 10
        });
      }
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Cycle through background videos
  useEffect(() => {
    const interval = setInterval(() => {
      setBackgroundIndex((prev) => (prev + 1) % films.length);
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  const openVideoModal = (film) => {
    setActiveVideo(film);
  };

  const closeVideoModal = () => {
    setActiveVideo(null);
  };

  // Close modal on escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (activeVideo) closeVideoModal();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeVideo]);

  return (
    <PageTransition>
      <CustomCursor />
      <div className="film-page-new" ref={containerRef}>
        {/* Background Video Montage */}
        <div className="film-background-montage">
          {films.map((film, index) => (
            <div
              key={film.id}
              className={`film-bg-video ${index === backgroundIndex ? 'active' : ''} ${hoveredCard === film.id ? 'focused' : ''}`}
              style={{ backgroundImage: `url(${film.thumbnail})` }}
            />
          ))}
          <div className="film-bg-overlay" />
        </div>

        {/* Header */}
        <div className="film-header">
          <button 
            className="film-close-btn"
            onClick={() => navigate('/')}
            aria-label="Close"
            data-testid="film-close-button"
          >
            <X size={24} />
          </button>
          
          <h1 className="film-title">film</h1>
        </div>

        {/* Floating Video Cards */}
        <div 
          className="film-cards-container"
          style={{
            '--rotate-x': `${mousePos.y}deg`,
            '--rotate-y': `${mousePos.x}deg`
          }}
        >
          {films.map((film, index) => (
            <div
              key={film.id}
              className={`film-floating-card ${hoveredCard === film.id ? 'hovered' : ''}`}
              style={{
                '--card-index': index,
                '--card-delay': `${index * 0.15}s`
              }}
              onMouseEnter={() => setHoveredCard(film.id)}
              onMouseLeave={() => setHoveredCard(null)}
              onClick={() => openVideoModal(film)}
              data-testid={`film-card-${film.id}`}
            >
              <div className="film-card-thumbnail">
                <img src={film.thumbnail} alt={film.title} />
                <div className="film-card-play">
                  <Play size={32} fill="white" />
                </div>
              </div>
              <div className="film-card-info">
                <h3>{film.title}</h3>
              </div>
            </div>
          ))}
        </div>

        {/* Video Modal */}
        {activeVideo && (
          <div 
            className="film-modal-overlay"
            onClick={closeVideoModal}
            ref={videoModalRef}
          >
            <div 
              className="film-modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <button 
                className="film-modal-close"
                onClick={closeVideoModal}
              >
                <X size={24} />
              </button>
              <div className="film-modal-video">
                <iframe
                  src={`${activeVideo.embedUrl}&autoplay=1`}
                  title={activeVideo.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
              <h3 className="film-modal-title">{activeVideo.title}</h3>
            </div>
          </div>
        )}
      </div>
    </PageTransition>
  );
};

export default FilmPage;
